import * as React from "react";
import { Tabs as TabsPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const Tabs = TabsPrimitive.Root;

const tabsListVariants = cva("inline-flex items-center text-muted-foreground", {
  variants: {
    variant: {
      default: "h-auto w-full justify-start gap-6 border-b border-border bg-transparent p-0",
      record: "h-auto gap-0 items-stretch bg-transparent p-0",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

function TabsList({
  ref,
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.List> &
  VariantProps<typeof tabsListVariants>) {
  return (
    <TabsPrimitive.List
      ref={ref}
      data-variant={variant}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
}

const tabsTriggerVariants = cva(
  "inline-flex items-center justify-center whitespace-nowrap font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "-mb-px h-10 border-b-2 border-transparent px-0 text-sm text-muted-foreground hover:text-foreground focus-visible:ring-offset-0 data-[state=active]:border-action data-[state=active]:font-semibold data-[state=active]:text-foreground",
        record:
          "relative h-10 flex-1 justify-center border border-border bg-card px-6 text-sm font-medium text-muted-foreground first:rounded-tl-md last:rounded-tr-md not-first:-ml-px hover:text-foreground focus-visible:ring-inset focus-visible:ring-offset-0 data-[state=active]:z-10 data-[state=active]:border-b-surface data-[state=active]:bg-surface data-[state=active]:text-foreground data-[state=active]:shadow-[inset_0_2px_0_0_var(--color-action)]",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TabsTrigger({
  ref,
  className,
  variant,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> &
  VariantProps<typeof tabsTriggerVariants>) {
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      data-slot="tabs-trigger"
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
}

function TabsContent({
  ref,
  className,
  ...props
}: React.ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      ref={ref}
      className={cn(
        "mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        "data-[state=active]:animate-in data-[state=active]:fade-in-0 data-[state=active]:duration-base data-[state=active]:ease-out",
        className,
      )}
      {...props}
    />
  );
}

export {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  tabsListVariants,
  tabsTriggerVariants,
};
